import { estimateLeg } from './format';
import type { LatLng, MapWaypoint } from './geo';
import type { PlaceSummary, Waypoint } from './types';

/**
 * Where a trip starts or ends: a place, or "my location" (the phone's position, which is
 * refreshed until the first stop is punched; lat/lng stay null until there's a fix).
 */
export interface TripPoint {
  label: string;
  lat: number | null;
  lng: number | null;
  mine?: boolean;
  /** For "my location": when the drive set off from here (Drive there to the first stop). */
  setOffAt?: string;
}

export interface TripEnds {
  start: TripPoint | null;
  end: TripPoint | null;
  /** Finish where you started (a loop). Wins over `end`. */
  endAtStart: boolean;
}

export const NO_ENDS: TripEnds = { start: null, end: null, endAtStart: false };

/** The label a shared trip carries for "start from my location". Its coordinates mean nothing. */
export const MY_LOCATION = 'My location';

export const myLocation = (at?: LatLng | null, setOff = false): TripPoint => ({
  label: MY_LOCATION,
  lat: at?.lat ?? null,
  lng: at?.lng ?? null,
  mine: true,
  ...(setOff ? { setOffAt: new Date().toISOString() } : {}),
});

// A night out: once you've set off, "my location" stays where you left from for this long.
const NIGHT_MS = 12 * 60 * 60 * 1000;
export const setOffTonight = (p: TripPoint | null, now = Date.now()) =>
  !!p?.setOffAt && now - Date.parse(p.setOffAt) < NIGHT_MS;
export const placePoint = (w: Waypoint): TripPoint => ({ label: w.label, lat: w.lat, lng: w.lng });

export function located(p: TripPoint | null | undefined): LatLng | null {
  return p && p.lat !== null && p.lng !== null ? { lat: p.lat, lng: p.lng } : null;
}

/** The point the trip finishes at, if any. */
export function finishOf(t: TripEnds): TripPoint | null {
  return t.endAtStart ? t.start : t.end;
}

export function finishLabel(t: TripEnds): string | null {
  if (t.endAtStart) return t.start && !t.start.mine ? `Back to the start (${t.start.label})` : 'Back to the start';
  return t.end?.label ?? null;
}

const same = (a: Waypoint, b: Waypoint) => Math.abs(a.lat - b.lat) < 1e-6 && Math.abs(a.lng - b.lng) < 1e-6;

/**
 * Reads the waypoints stored on a trip (saved on this device before start/end existed, or on a
 * shared trip): the first is the start, the last the end, and an end on top of the start is a loop.
 */
export function endsFromWaypoints(wps: Waypoint[] | undefined): TripEnds {
  if (!wps?.length) return NO_ENDS;
  const read = (w: Waypoint): TripPoint => (w.label === MY_LOCATION ? myLocation() : placePoint(w));
  const start = read(wps[0]);
  if (wps.length === 1) return { start, end: null, endAtStart: false };
  const last = wps[wps.length - 1];
  if (same(wps[0], last)) return { start, end: null, endAtStart: true };
  return { start, end: read(last), endAtStart: false };
}

/**
 * The waypoints sent with a shared trip: [start, end], or [start] without an end. "My location"
 * (and no start at all, which means the same thing) goes as a label only, pinned to the first stop,
 * so the link never carries anyone's position.
 */
export function waypointsFor(t: TripEnds, stops: PlaceSummary[]): Waypoint[] {
  const anchor = stops[0] ?? null;
  const send = (p: TripPoint | null): Waypoint | null => {
    if (p && !p.mine && p.lat !== null && p.lng !== null) return { label: p.label, lat: p.lat, lng: p.lng };
    return anchor ? { label: MY_LOCATION, lat: anchor.lat, lng: anchor.lng } : null;
  };
  if (!t.start && !t.end && !t.endAtStart) return [];
  const start = send(t.start);
  if (!start) return [];
  if (t.endAtStart) return [start, start];
  const end = t.end ? send(t.end) : null;
  return end ? [start, end] : [start];
}

/** Map props for a trip's start and finish: the line's two ends and their S / F pins. */
export function mapEnds(t: TripEnds, { hideMine = false } = {}) {
  const from = located(t.start);
  const to = located(finishOf(t));
  const waypoints: MapWaypoint[] = [];
  if (from && !(hideMine && t.start?.mine)) waypoints.push({ ...from, label: t.endAtStart ? `Start and finish: ${t.start!.label}` : `Start: ${t.start!.label}`, tag: 'S' });
  if (to && !t.endAtStart) waypoints.push({ ...to, label: `Finish: ${t.end!.label}`, tag: 'F' });
  const fit = [from, to].filter((p): p is LatLng => !!p).map((p) => [p.lat, p.lng] as [number, number]);
  return { routeFrom: from, routeTo: to, waypoints, fit };
}

export type Leg = ReturnType<typeof estimateLeg>;

/** Start to stop 1, stop to stop, and last stop to the end. Unknown ends give null legs. */
export function tripLegs(t: TripEnds, stops: PlaceSummary[]) {
  const from = located(t.start);
  const to = located(finishOf(t));
  const first = from && stops.length ? estimateLeg(from, stops[0]) : null;
  const between = stops.slice(0, -1).map((s, i) => estimateLeg(s, stops[i + 1]));
  const last = to && stops.length ? estimateLeg(stops[stops.length - 1], to) : null;
  const all = [first, ...between, last].filter((l): l is Leg => !!l);
  const drive = all.filter((l) => !l.walk);
  return {
    first,
    between,
    last,
    driveMiles: drive.reduce((a, l) => a + l.miles, 0),
    driveMinutes: drive.reduce((a, l) => a + l.minutes, 0),
  };
}

// Google Maps URLs take up to nine waypoints between the origin and the destination.
const MAX_WAYPOINTS = 9;

/**
 * Directions for the whole trip in Google Maps: from the start (or wherever you are, for "my
 * location"), through the stops, to the end (or the last stop).
 */
export function googleMapsTripUrl(t: TripEnds, stops: PlaceSummary[]): { url: string; stopsIncluded: number } | null {
  if (!stops.length) return null;
  const ll = (p: LatLng) => `${p.lat},${p.lng}`;
  const finish = located(finishOf(t));
  // Too many stops for one link: go as far as Google takes and finish at the last stop that fits.
  const fits = stops.length <= (finish ? MAX_WAYPOINTS : MAX_WAYPOINTS + 1);
  const shown = fits ? stops : stops.slice(0, MAX_WAYPOINTS + 1);
  const points: LatLng[] = fits && finish ? [...shown, finish] : shown;
  const params = new URLSearchParams({ api: '1', travelmode: 'driving', destination: ll(points[points.length - 1]) });
  const from = t.start && !t.start.mine ? located(t.start) : null;
  if (from) params.set('origin', ll(from));
  if (points.length > 1) params.set('waypoints', points.slice(0, -1).map(ll).join('|'));
  return { url: `https://www.google.com/maps/dir/?${params.toString()}`, stopsIncluded: shown.length };
}
